import api from '@/api'
import status from '@/utils/status'
import format from '@/utils/format'

// 카테고리 4종 (SOIL/WATER/AIR/DISPOSAL) - utils/options.js anaDeptOptions 기준
const CATEGORIES = [
	{ value: 'SOIL', text: '토양', color: '#B09269' },
	{ value: 'WATER', text: '수질', color: '#85AAD6' },
	{ value: 'AIR', text: '대기', color: '#D88FA4' },
	{ value: 'DISPOSAL', text: '폐기물', color: '#48959C' }
]

const DOC_TYPE_COLORS = {
	EA: '#535CA8',
	EB: '#E09C41',
	EC: '#DE6B7A',
	ED: '#6EBF60',
	EE: '#FF922D',
	EF: '#FEC741',
	EG: '#66BCEA',
	EI: '#59C1B3',
	EK: '#BE4467',
	EL: '#6C60CD',
	QA: '#9A9A9A',
	EM: '#48959C',
	EJ: '#B09269',
	'EO-A': '#00884F',
	'EO-B': '#024AC9',
	'EM-A': '#2A9D8F',
	'EM-B': '#E9C46A',
	// 폐기물 코드군 (0-3)
	EN: '#48959C',
	EP: '#264653',
	'EM-C': '#E76F51',
}
const DOC_TYPE_FALLBACK_COLORS = [
	'#535CA8', '#E09C41', '#DE6B7A', '#6EBF60', '#FF922D',
	'#FEC741', '#66BCEA', '#59C1B3', '#BE4467', '#6C60CD'
]

const emptyProjectSituation = () => ({
	all: format.number(0),
	isApprove: format.number(0),
	acceptedCount: format.number(0),
	analysisCount: format.number(0),
	reviewCount: format.number(0),
	approvedDoneCount: format.number(0),
	statusSummary: {},
	projectChartsets: { labels: [''], datasets: [] },
	deptRatio: { labels: [], datasets: [] },
	deptSummary: { labels: [], datasets: [] },
	topDeptByProject: []
})

const emptyAmountSituation = () => ({
	all: format.number(0),
	amount: `${format.number(0)}원`,
	unpaidAmount: `${format.number(0)}원`,
	contractAmount: `${format.number(0)}원`,
	publishedAmount: `${format.number(0)}원`,
	paidAmount: `${format.number(0)}원`,
	deptSaleRatio: { labels: [], datasets: [] },
	docTypeRatio: { labels: [], datasets: [] },
	topDeptBySale: []
})

const state = {
	projectSituation: emptyProjectSituation(),
	amountSituation: emptyAmountSituation()
}

const getters = {
	projectSituation: state => state.projectSituation,
	amountSituation: state => state.amountSituation
}

const buildTopDepts = (ratio = {}, limit = 3) =>
	CATEGORIES.map(c => ({
		label: c.text,
		value: c.value,
		count: Number(ratio[c.value] || 0)
	}))
		.filter(item => item.count > 0)
		.sort((a, b) => b.count - a.count)
		.slice(0, limit)

const sumStatusCounts = (statusSummary = {}, keys = []) =>
	keys.reduce((acc, key) => acc + Number(statusSummary[key] || 0), 0)

const buildStackedChartset = (statusSummary = {}) => {
	const datasets = status.project.map(item => ({
		label: item.text,
		data: [Number(statusSummary[item.value] || 0)],
		backgroundColor: item.backgroundColor
	}))
	if (datasets.length) {
		datasets[0].borderSkipped = false
		datasets[0].borderRadius = {
			topLeft: 5,
			bottomLeft: 5
		}
	}
	return {
		labels: [''],
		datasets
	}
}

const buildDeptRatioChartset = (deptRatio = {}) => ({
	labels: CATEGORIES.map(c => c.text),
	datasets: [{
		data: CATEGORIES.map(c => Number(deptRatio[c.value] || 0)),
		backgroundColor: CATEGORIES.map(c => c.color)
	}]
})

const buildDeptSummaryChartset = (deptSummary = {}) => {
	const datasets = status.project.map(item => ({
		label: item.text,
		data: CATEGORIES.map(c => Number((deptSummary[c.value] || {})[item.value] || 0)),
		backgroundColor: item.backgroundColor
	}))
	return {
		labels: CATEGORIES.map(c => c.text),
		datasets
	}
}

const buildDeptSaleRatioChartset = (deptSaleRatio = {}) => ({
	labels: CATEGORIES.map(c => c.text),
	datasets: [{
		data: CATEGORIES.map(c => Number(deptSaleRatio[c.value] || 0)),
		backgroundColor: CATEGORIES.map(c => c.color)
	}]
})

const buildDocTypeRatioChartset = (docTypeRatio = {}) => {
	const keys = Object.keys(docTypeRatio || {}).filter(key => Number(docTypeRatio[key]) > 0)
	return {
		labels: keys,
		datasets: [{
			data: keys.map(key => Number(docTypeRatio[key] || 0)),
			backgroundColor: keys.map((key, idx) =>
				DOC_TYPE_COLORS[key] || DOC_TYPE_FALLBACK_COLORS[idx % DOC_TYPE_FALLBACK_COLORS.length]
			)
		}]
	}
}

const mutations = {
	setProjectSituation: (state, data) => {
		if (!data) {
			state.projectSituation = emptyProjectSituation()
			return
		}
		const statusSummary = data.statusSummary || {}
		state.projectSituation = {
			all: format.number(Number(data.all || 0)),
			isApprove: format.number(Number(data.isApprove || 0)),
			acceptedCount: format.number(Number(statusSummary.WORK_ACCEPTED || 0)),
			analysisCount: format.number(sumStatusCounts(statusSummary, ['WORK_ANALYSIS'])),
			reviewCount: format.number(sumStatusCounts(statusSummary, [
				'WORK_DIRECTOR_REVIEW',
				'WORK_IMPLEMENTATION_REVIEW'
			])),
			approvedDoneCount: format.number(sumStatusCounts(statusSummary, [
				'WORK_APPROVAL',
				'WORK_COMPLETE'
			])),
			statusSummary,
			projectChartsets: buildStackedChartset(statusSummary),
			deptRatio: buildDeptRatioChartset(data.deptRatio),
			deptSummary: buildDeptSummaryChartset(data.deptSummary),
			topDeptByProject: buildTopDepts(data.deptRatio)
		}
	},
	setAmountSituation: (state, data) => {
		if (!data) {
			state.amountSituation = emptyAmountSituation()
			return
		}
		const amount = Number(data.amount || 0)
		const unpaid = Number(data.unpaidAmount || 0)
		const contract = Number(data.contractAmount ?? data.all ?? 0)
		const published = Number(data.publishedAmount ?? amount ?? 0)
		const paid = Number(data.paidAmount ?? 0)
		state.amountSituation = {
			all: format.number(Number(data.all || 0)),
			amount: `${format.number(amount)}원`,
			unpaidAmount: `${format.number(unpaid)}원`,
			contractAmount: `${format.number(contract)}원`,
			publishedAmount: `${format.number(published)}원`,
			paidAmount: `${format.number(paid)}원`,
			deptSaleRatio: buildDeptSaleRatioChartset(data.deptSaleRatio),
			docTypeRatio: buildDocTypeRatioChartset(data.docTypeRatio),
			topDeptBySale: buildTopDepts(data.deptSaleRatio)
		}
	},
	resetProjectSituation: state => {
		state.projectSituation = emptyProjectSituation()
	},
	resetAmountSituation: state => {
		state.amountSituation = emptyAmountSituation()
	}
}

// 서버는 startDatetime/endDatetime(+선택 category)만 인식. 빈 값은 전송하지 않는다.
const buildQuery = (params = {}) => {
	const { startDatetime, endDatetime, category } = params
	const query = {}
	if (startDatetime) query.startDatetime = startDatetime
	if (endDatetime) query.endDatetime = endDatetime
	if (category) query.category = category
	return query
}

const actions = {
	//업무 현황 - 업무 숫자, 승인 대기 숫자, 전체 업무 현황, 부서별 업무 비율, 부서 별 업무 현황
	loadProjectSituation: async ({ commit }, params) => {
		try {
			const res = await api.dashboard.getProjectSituation(buildQuery(params))
			commit('setProjectSituation', res)
			return res
		} catch (e) {
			commit('resetProjectSituation')
			throw e
		}
	},
	//매출 현황 - 접수된 업무, 매출 총액, 미수금, 부서별 매출 비율, 문서 유형별 매출 비율
	loadAmountSituation: async ({ commit }, params) => {
		try {
			const res = await api.dashboard.getAmountSituation(buildQuery(params))
			commit('setAmountSituation', res)
			return res
		} catch (e) {
			commit('resetAmountSituation')
			throw e
		}
	}
}

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,
	CATEGORIES
}
